import dayjs from 'dayjs';
// Extension spelled out, like the two plugins below: webpack resolves it either
// way, Node does not, and the test mirror runs this file under Node.
import isBetween from 'dayjs/plugin/isBetween.js';
import utc from 'dayjs/plugin/utc.js';
import timezone from 'dayjs/plugin/timezone.js';
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isBetween);

const tz = 'Europe/Bucharest';

const DAY_START = 7; // 07:00 local
const NIGHT_START = 19; // 19:00 local

/**
 * A local wall-clock time, resolved to the right UTC instant for that date.
 *
 * Built by PARSING a local date-time string in the zone rather than by
 * arithmetic on a moment. dayjs keeps the offset it started with when you
 * .add()/.hour() across a DST boundary, so `startOf('day').hour(7)` quietly
 * yields 06:00 or 08:00 on the two changeover days each year — which is how a
 * shift window ends up an hour out twice a year.
 */
const atLocal = (dateStr, hour) =>
  dayjs.tz(`${dateStr} ${String(hour).padStart(2, '0')}:00:00`, tz);

const dateOf = d => d.format('YYYY-MM-DD');

/**
 * The shift in progress, as a UTC range for InfluxDB.
 *
 * Shifts are 07:00–19:00 and 19:00–07:00 in LOCAL time; Influx stores UTC, so
 * the boundaries are defined locally and converted once, here.
 *
 * The night shift crosses midnight, which means the same local hour belongs to
 * two different calendar days depending on which side of midnight you are on:
 * at 23:00 the shift began at 19:00 TODAY, at 03:00 it began at 19:00
 * YESTERDAY. The previous version ignored that and returned yesterday-19:00 to
 * tomorrow-07:00 in both cases — a 36-hour window, three times the shift, which
 * is why every machine's night charts were wrong while the day was fine.
 */
const getShiftStartEndTime = () => {
  const now = dayjs().tz(tz);
  const hour = now.hour();
  const today = dateOf(now);

  // Half-open on purpose: 07:00:00 exactly starts the day shift, 19:00:00
  // exactly starts the night one, and no instant belongs to both or neither.
  if (hour >= DAY_START && hour < NIGHT_START) {
    return {
      startTime: atLocal(today, DAY_START).utc().toISOString(),
      endTime: atLocal(today, NIGHT_START).utc().toISOString()
    };
  }

  const beforeMidnight = hour >= NIGHT_START;
  const startDate = beforeMidnight ? today : dateOf(now.subtract(1, 'day'));
  const endDate = beforeMidnight ? dateOf(now.add(1, 'day')) : today;

  return {
    startTime: atLocal(startDate, NIGHT_START).utc().toISOString(),
    endTime: atLocal(endDate, DAY_START).utc().toISOString()
  };
};

export default getShiftStartEndTime;

/**
 * Step to the neighbouring shift.
 *
 * Derived from the start boundary rather than by adding 12 hours: on the two
 * DST nights a shift is 11 or 13 real hours, and stepping by a fixed 12 would
 * walk the window off the 07:00/19:00 grid and leave it there.
 *
 * @param direction +1 for the next shift, -1 for the previous one
 */
export function updateTimeRange(prevRange, direction) {
  const startLocal = dayjs.utc(prevRange.startTime).tz(tz);
  const isDayShift = startLocal.hour() < NIGHT_START;
  const startDate = dateOf(startLocal);

  if (isDayShift) {
    // day D → night D (forward) or night D-1 (back)
    const nightDate = direction === 1 ? startDate : dateOf(startLocal.subtract(1, 'day'));
    const morningDate = direction === 1 ? dateOf(startLocal.add(1, 'day')) : startDate;
    return {
      startTime: atLocal(nightDate, NIGHT_START).utc().toISOString(),
      endTime: atLocal(morningDate, DAY_START).utc().toISOString()
    };
  }

  // night starting D → day D+1 (forward) or day D (back)
  const dayDate = direction === 1 ? dateOf(startLocal.add(1, 'day')) : startDate;
  return {
    startTime: atLocal(dayDate, DAY_START).utc().toISOString(),
    endTime: atLocal(dayDate, NIGHT_START).utc().toISOString()
  };
}

/**
 * Check if a time range represents "live" data (current shift).
 * If endTime is in the past (> 15 min ago), data is historical — no need to poll.
 */
export function isLiveTimeRange(endTime) {
  if (!endTime) return true;
  const end = dayjs(endTime);
  const now = dayjs();
  return end.isAfter(now.subtract(15, 'minute'));
}
